import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from '../../../../../contexts/I18nProvider';
import { X, ChevronDown, ChevronUp, Users, User, AlertTriangle } from 'lucide-react';
import { PListOrderDto } from '../../../../../services/plistOrderService';
import { UserProfileDto } from '../../../../../types/userProfile';
import { ShipmentLineEditor } from '../../../shared/ShipmentLineEditor';
import { ShipmentLineRow, ShipmentLineDto, TransitPackagingDto } from '../../../../../types/shipmentLine';
import { PalletSummaryDto } from '../../../../../types/picking';
import { shipmentLineService } from '../../../../../services/shipmentLineService';
import { transitPackagingService } from '../../../../../services/transitPackagingService';
import { packingService } from '../../../../../services/packingService';
import { settingsService } from '../../../../../services/settingsService';
import { DeliveryMethodDto, PackZoneDto } from '../../../../../types/packing';
import { HelpHint } from '../../../../common/HelpHint';
import { SearchableSelect } from '../../../../common/SearchableSelect';
import { useHideConsolidationPrompt } from '../../../../../hooks/useHideConsolidationPrompt';
import { translateApiError } from '../../../../../utils/apiError';

interface DeliveryInfoData {
  totalVolume: string;
  totalWeight: string;
  numOfPackages: string;
  numOfPallets: string;
  carrierName: string;
  deliveryMethodNo: number | null;
  packZoneID: string;
  packageRemark: string;
}

interface PListOrderDeliveryData extends DeliveryInfoData {
  orderNo: number;
  expanded: boolean;
  shipmentLinesExpanded: boolean;
  shipmentLines: ShipmentLineRow[];
  // Address info for display
  customerName?: string | null;
  delAddress1?: string | null;
  delCity?: string | null;
  // K3: Used to pick between domestic and international default delivery method
  delCountry?: string | null;
}

type DeliveryMode = 'single' | 'consolidate' | 'per-order' | 'forced-per-order';
type AggregationMethod = 'copy' | 'divide';

interface DeliveryInfoModalProps {
  isOpen: boolean;
  pListId: number;
  companyNo: string;
  pListOrders: PListOrderDto[];
  userProfile: UserProfileDto | null;
  onConfirm: (
    pListData: DeliveryInfoData,
    pListOrdersData: Map<number, DeliveryInfoData>,
    shipmentLinesData?: Map<number, ShipmentLineRow[]>,
    consolidate?: boolean
  ) => Promise<void>;
  onClose: () => void;
  /** Paller fra plukket (36334224): grunnlag for auto-genererte kollilinjer i lastbærer-fokus. */
  pallets?: PalletSummaryDto[];
}

// Helper to create an address key for comparison
const getAddressKey = (order: PListOrderDto): string => {
  return [
    order.customerNo || '',
    order.delName || '',
    order.delAddress1 || '',
    order.delPostNo || '',
    order.delCity || ''
  ].join('|').toLowerCase().trim();
};

// Check if all orders have the same delivery address
const allOrdersSameAddress = (orders: PListOrderDto[]): boolean => {
  if (orders.length <= 1) return true;
  const firstKey = getAddressKey(orders[0]);
  return orders.every(o => getAddressKey(o) === firstKey);
};

export const DeliveryInfoModal: React.FC<DeliveryInfoModalProps> = ({
  isOpen,
  pListId,
  companyNo,
  pListOrders,
  userProfile,
  onConfirm,
  onClose,
  pallets
}) => {
  const { t } = useTranslation();
  // Determine scenario
  const scenario = useMemo(() => {
    if (pListOrders.length === 0) return 'no-orders';
    if (pListOrders.length === 1) return 'single';
    return allOrdersSameAddress(pListOrders) ? 'multi-same-address' : 'multi-different-address';
  }, [pListOrders]);

  // Delivery mode state
  const [deliveryMode, setDeliveryMode] = useState<DeliveryMode>('single');
  const [aggregationMethod, setAggregationMethod] = useState<AggregationMethod | null>(null);
  const [showModeSelection, setShowModeSelection] = useState(false);
  const [showAggregationSelection, setShowAggregationSelection] = useState(false);

  // Innstilling 'picking.hideConsolidationPrompt' (per tenant, default av):
  // skjuler samlastings-/fordelingsvalget — modalen går rett til per ordre-modus.
  // Ordre som skal sendes samlet håndteres da i pakkemodulen i stedet.
  const hideConsolidationPrompt = useHideConsolidationPrompt();

  // Initialize delivery mode based on scenario
  useEffect(() => {
    if (scenario === 'single' || scenario === 'no-orders') {
      setDeliveryMode('single');
      setShowModeSelection(false);
    } else if (scenario === 'multi-different-address') {
      setDeliveryMode('forced-per-order');
      setShowModeSelection(false);
    } else if (scenario === 'multi-same-address') {
      if (hideConsolidationPrompt) {
        // Samlastings-spørsmålet er slått av for tenanten: gå rett til per ordre-modus
        // (ingen samlasting, ingen kopier/del likt-fordeling).
        setDeliveryMode('per-order');
        setShowModeSelection(false);
      } else {
        // Show mode selection for same address
        setShowModeSelection(true);
        setDeliveryMode('consolidate'); // Default to consolidate
      }
    }
  }, [scenario, hideConsolidationPrompt]);

  // Dropdown data
  const [deliveryMethods, setDeliveryMethods] = useState<DeliveryMethodDto[]>([]);
  const [packZones, setPackZones] = useState<PackZoneDto[]>([]);

  // Default pack zone from operator's profile — used when neither PList nor order
  // already has a zone selected. Operator can still override in the dropdown.
  const defaultPackZoneFromProfile = userProfile?.defaultPackZoneFilter || '';

  const [pListData, setPListData] = useState<DeliveryInfoData>({
    totalVolume: '',
    totalWeight: '',
    numOfPackages: '',
    numOfPallets: '',
    carrierName: '',
    deliveryMethodNo: null,
    packZoneID: defaultPackZoneFromProfile,
    packageRemark: ''
  });

  const [pListOrdersData, setPListOrdersData] = useState<PListOrderDeliveryData[]>(
    pListOrders.map(order => ({
      orderNo: order.orderNo,
      totalVolume: order.totalVolume?.toString() || '',
      totalWeight: order.totalWeight?.toString() || '',
      numOfPackages: order.numOfPackages?.toString() || '',
      numOfPallets: order.numOfPallets?.toString() || '',
      carrierName: order.carrierName || '',
      deliveryMethodNo: order.deliveryMethodNo ?? null,
      packZoneID: order.packZoneID || defaultPackZoneFromProfile,
      packageRemark: order.packageRemark || '',
      expanded: false,
      shipmentLinesExpanded: false,
      shipmentLines: [],
      customerName: order.customerName,
      delAddress1: order.delAddress1,
      delCity: order.delCity,
      delCountry: order.delCountry
    }))
  );

  const [shipmentLinesForConsolidation, setShipmentLinesForConsolidation] = useState<ShipmentLineRow[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingShipmentLines, setIsLoadingShipmentLines] = useState(false);

  // Pre-fill defaults from settings (Profile -> System -> Default).
  // Applied ONLY to fields that are still empty after Compass/order initialisation,
  // so values already set during pick creation are never overwritten.
  useEffect(() => {
    let cancelled = false;

    const loadDefaults = async () => {
      let defaultDeliveryMethodNo: number | null = null;
      let defaultDeliveryMethodNoInternational: number | null = null;
      let homeCountryCode = 'NO';
      let defaultTotalWeight = '';
      let defaultNumOfPackages = '';

      try {
        const dm = await settingsService.getEffectiveSetting('picking.defaultDeliveryMethodNo');
        if (dm?.currentValue) {
          const parsed = parseInt(dm.currentValue, 10);
          if (!isNaN(parsed)) defaultDeliveryMethodNo = parsed;
        }
      } catch (err) {
        console.warn('Could not load picking.defaultDeliveryMethodNo, using none:', err);
      }

      try {
        const dmi = await settingsService.getEffectiveSetting('picking.defaultDeliveryMethodNoInternational');
        if (dmi?.currentValue) {
          const parsed = parseInt(dmi.currentValue, 10);
          if (!isNaN(parsed)) defaultDeliveryMethodNoInternational = parsed;
        }
      } catch (err) {
        console.warn('Could not load picking.defaultDeliveryMethodNoInternational, using none:', err);
      }

      try {
        const hc = await settingsService.getEffectiveSetting('picking.homeCountryCode');
        if (hc?.currentValue && hc.currentValue.trim()) {
          homeCountryCode = hc.currentValue.trim().toUpperCase();
        }
      } catch (err) {
        console.warn('Could not load picking.homeCountryCode, using NO:', err);
      }

      try {
        const tw = await settingsService.getEffectiveSetting('picking.defaultTotalWeight');
        if (tw?.currentValue) {
          // Stored as string in settings — accept comma or dot decimal separator
          const normalized = tw.currentValue.replace(',', '.');
          const parsed = parseFloat(normalized);
          if (!isNaN(parsed)) defaultTotalWeight = parsed.toString();
        }
      } catch (err) {
        console.warn('Could not load picking.defaultTotalWeight, using none:', err);
      }

      try {
        const np = await settingsService.getEffectiveSetting('picking.defaultNumOfPackages');
        if (np?.currentValue) {
          const parsed = parseInt(np.currentValue, 10);
          if (!isNaN(parsed) && parsed >= 0) defaultNumOfPackages = parsed.toString();
        }
      } catch (err) {
        console.warn('Could not load picking.defaultNumOfPackages, using none:', err);
      }

      if (cancelled) return;
      if (
        defaultDeliveryMethodNo == null &&
        defaultDeliveryMethodNoInternational == null &&
        !defaultTotalWeight &&
        !defaultNumOfPackages
      ) {
        return;
      }

      // K3: Pick domestic vs international default based on country.
      // International default is used when country is set AND differs from homeCountryCode.
      // Falls back to domestic default if international is not configured.
      const pickDmForCountry = (country?: string | null): number | null => {
        const trimmed = country?.trim().toUpperCase();
        if (trimmed && trimmed !== homeCountryCode && defaultDeliveryMethodNoInternational != null) {
          return defaultDeliveryMethodNoInternational;
        }
        return defaultDeliveryMethodNo;
      };

      // For the PList-level default we use the first order's country as representative.
      const firstOrderCountry = pListOrders[0]?.delCountry;
      const pListDefaultDm = pickDmForCountry(firstOrderCountry);

      setPListData(prev => ({
        ...prev,
        deliveryMethodNo: prev.deliveryMethodNo ?? pListDefaultDm,
        totalWeight: prev.totalWeight || defaultTotalWeight,
        numOfPackages: prev.numOfPackages || defaultNumOfPackages,
      }));

      setPListOrdersData(prev => prev.map(o => ({
        ...o,
        deliveryMethodNo: o.deliveryMethodNo ?? pickDmForCountry(o.delCountry),
        totalWeight: o.totalWeight || defaultTotalWeight,
        numOfPackages: o.numOfPackages || defaultNumOfPackages,
      })));
    };

    loadDefaults();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Determine if current delivery method requires single parcel mode (1 kolli = 1 sending)
  const isSingleParcelMode = useMemo(() => {
    const methodNo = pListData.deliveryMethodNo;
    if (!methodNo) return false;
    const method = deliveryMethods.find(m => m.deliveryMethodNo === methodNo);
    return method?.singleParcelDelivery ?? false;
  }, [pListData.deliveryMethodNo, deliveryMethods]);

  // ─── Lastbærer-fokus (Teamwork 36334219, setting delivery.carrierLineFocus, default AV) ───
  // Når PÅ: Transport-detaljer er sannhetskilden — hodefeltene (kolli/paller/vekt/volum)
  // BEREGNES fra kollilinjene og låses så lenge linjer finnes. Uten linjer er hodefeltene
  // fritt redigerbare (= dagens oppførsel). Andre tenants (setting av) er 100 % uberørt.
  const [carrierLineFocus, setCarrierLineFocus] = useState(false);
  useEffect(() => {
    let cancelled = false;
    settingsService.getEffectiveSetting('delivery.carrierLineFocus')
      .then(s => {
        if (cancelled) return;
        const val = s?.currentValue?.toString().toLowerCase();
        setCarrierLineFocus(val === 'true' || val === '1' || val === 'ja');
      })
      .catch(err => console.warn('Could not load delivery.carrierLineFocus:', err));
    return () => { cancelled = true; };
  }, []);

  // Auto-generate ShipmentLines when numOfPackages changes in single parcel mode
  const generateSingleParcelLines = useCallback((numPackages: number, orderNo: number, totalWeight: string) => {
    const count = Math.max(1, Math.min(numPackages, 50)); // Cap at 50
    const weightPerParcel = totalWeight ? parseFloat(totalWeight) / count : null;
    const lines: ShipmentLineRow[] = [];
    for (let i = 0; i < count; i++) {
      lines.push({
        tempId: -(Date.now() + i),
        lineID: '',
        transitPackagingID: null,
        transitPackagingDescr: null,
        description1: `Kolli ${i + 1}`,
        numOfPackages: 1,
        volume: null,
        weight: weightPerParcel ? Math.round(weightPerParcel * 100) / 100 : null,
        height: null,
        width: null,
        length: null,
        pListID: pListId,
        orderNo: orderNo,
        sorting: i,
        isNew: true
      });
    }
    return lines;
  }, [pListId]);

  // When numOfPackages or totalWeight changes in single parcel mode, auto-update ShipmentLines
  useEffect(() => {
    // Lastbærer-fokus (36334219): linjene er sannhetskilden — hode→linje-generering
    // (single parcel) deaktiveres for å unngå sirkulær synk. Linje→hode-synken under tar over.
    if (carrierLineFocus) return;
    if (!isSingleParcelMode) return;

    const numPackages = parseInt(pListData.numOfPackages) || 0;
    if (numPackages <= 0) return;

    // For single order scenario, update the first order's shipment lines
    if (scenario === 'single' && pListOrders[0]) {
      const orderNo = pListOrders[0].orderNo;
      const newLines = generateSingleParcelLines(numPackages, orderNo, pListData.totalWeight);
      setPListOrdersData(prev => prev.map(order =>
        order.orderNo === orderNo
          ? { ...order, shipmentLines: newLines, shipmentLinesExpanded: true }
          : order
      ));
    }
  }, [carrierLineFocus, isSingleParcelMode, pListData.numOfPackages, pListData.totalWeight, scenario, pListOrders, generateSingleParcelLines]);

  // Lastbærer-fokus: aggregering av hodefelt fra kollilinjene. Paller = sum ANTALL for
  // linjer med pall-type bærer (beskrivelse inneholder «pall» — Pall/Halvpall hos Haucon).
  const aggregateFromLines = useCallback((lines: ShipmentLineRow[] | undefined) => {
    if (!lines || lines.length === 0) return null;
    // Samme formler som hasPackageDetailsMismatch — vekt/volum er per kolli × antall.
    const sumPackages = lines.reduce((s, l) => s + (Number(l.numOfPackages) || 0), 0);
    const sumWeight = lines.reduce((s, l) => s + (Number(l.weight) || 0) * (Number(l.numOfPackages) || 0), 0);
    const sumVolume = lines.reduce((s, l) => s + (Number(l.volume) || 0) * (Number(l.numOfPackages) || 0), 0);
    const sumPallets = lines.reduce((s, l) =>
      s + ((l.transitPackagingDescr || '').toLowerCase().includes('pall') ? (Number(l.numOfPackages) || 0) : 0), 0);
    return {
      numOfPackages: sumPackages > 0 ? String(sumPackages) : '',
      totalWeight: sumWeight > 0 ? (Math.round(sumWeight * 100) / 100).toString() : '',
      totalVolume: sumVolume > 0 ? (Math.round(sumVolume * 10000) / 10000).toString() : '',
      numOfPallets: sumPallets > 0 ? String(sumPallets) : ''
    };
  }, []);

  // Linjene for gjeldende scenario (samme oppløsning som mismatch-sjekken)
  const focusScenarioLines: ShipmentLineRow[] | undefined =
    (deliveryMode === 'single' || scenario === 'single')
      ? pListOrdersData[0]?.shipmentLines
      : (deliveryMode === 'consolidate' && aggregationMethod)
        ? shipmentLinesForConsolidation
        : undefined;

  const headAggregatedLock = carrierLineFocus && (focusScenarioLines?.length ?? 0) > 0;

  // Linje→hode-synk (single/konsolidert): hold hodefeltene lik linjesummene
  useEffect(() => {
    if (!headAggregatedLock) return;
    const agg = aggregateFromLines(focusScenarioLines);
    if (!agg) return;
    setPListData(prev => {
      if (prev.numOfPackages === agg.numOfPackages && prev.totalWeight === agg.totalWeight
        && prev.totalVolume === agg.totalVolume && prev.numOfPallets === agg.numOfPallets) {
        return prev;
      }
      return { ...prev, ...agg };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headAggregatedLock, focusScenarioLines, aggregateFromLines]);

  // Linje→hode-synk per ordre (per-order-modus)
  useEffect(() => {
    if (!carrierLineFocus) return;
    if (deliveryMode !== 'per-order' && deliveryMode !== 'forced-per-order') return;
    setPListOrdersData(prev => prev.map(order => {
      const agg = aggregateFromLines(order.shipmentLines);
      if (!agg) return order;
      if (order.numOfPackages === agg.numOfPackages && order.totalWeight === agg.totalWeight
        && order.totalVolume === agg.totalVolume && order.numOfPallets === agg.numOfPallets) {
        return order;
      }
      return { ...order, ...agg };
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carrierLineFocus, deliveryMode, pListOrdersData.map(o => o.shipmentLines).flat().length, aggregateFromLines]);

  // Fokus-layout: åpne Transport-detaljer automatisk når fokus er på
  useEffect(() => {
    if (!carrierLineFocus) return;
    setPListOrdersData(prev => prev.map(order =>
      order.shipmentLinesExpanded ? order : { ...order, shipmentLinesExpanded: true }
    ));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carrierLineFocus, pListOrdersData.length]);

  // ─── Auto-genererte kollilinjer fra pallene (36334224) ───
  // Lastbærer-fokus + 1:1-scenario + plukket mot pall + ingen eksisterende linjer:
  // generer én kollilinje per pall — TYPE fra pallens bærer, L/B fra bærer-registeret,
  // HØYDE alltid åpen, vekt = varevekt (sum OrderSSCC.GrossWeight) + bærerens tara
  // (bruttovekt, vedtak fra gjennomgangen). Operatøren kan justere alt før lagring.
  // Eksisterende/persisterte linjer overskrives ALDRI.
  useEffect(() => {
    if (!carrierLineFocus || !isOpen) return;
    if (isLoadingShipmentLines) return;
    if (!pallets || pallets.length === 0) return;
    if (!(deliveryMode === 'single' || scenario === 'single')) return;
    const order = pListOrdersData[0];
    if (!order || (order.shipmentLines?.length ?? 0) > 0) return;

    const realPallets = pallets.filter(p => p.totalItems > 0);
    if (realPallets.length === 0) return;

    let cancelled = false;
    (async () => {
      let carriers: TransitPackagingDto[] = [];
      try {
        carriers = await transitPackagingService.getAll();
      } catch (err) {
        console.warn('Could not load carriers for pallet line generation:', err);
      }
      if (cancelled) return;

      const lines: ShipmentLineRow[] = realPallets.map((pallet, i) => {
        const carrier = carriers.find(c => c.transitPackagingID === pallet.transitPackagingID);
        const tare = carrier?.weight || 0;
        const itemsWeight = pallet.totalWeight || 0;
        const gross = itemsWeight + tare;
        return {
          tempId: -(2000 + i),
          lineID: '',
          transitPackagingID: pallet.transitPackagingID ?? null,
          transitPackagingDescr: carrier?.transitPackagingDescr ?? pallet.transitPackagingDescr ?? null,
          description1: pallet.sscc,
          numOfPackages: 1,
          volume: null,
          weight: gross > 0 ? Math.round(gross * 100) / 100 : null,
          height: null, // pallhøyde varierer — alltid åpen (vedtak 36334224)
          width: carrier?.width ?? null,
          length: carrier?.length ?? null,
          pListID: pListId,
          orderNo: order.orderNo,
          sorting: i,
          isNew: true
        } as ShipmentLineRow;
      });

      setPListOrdersData(prev => prev.map(o =>
        o.orderNo === order.orderNo && (o.shipmentLines?.length ?? 0) === 0
          ? { ...o, shipmentLines: lines, shipmentLinesExpanded: true }
          : o
      ));
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carrierLineFocus, isOpen, isLoadingShipmentLines, pallets, deliveryMode, scenario,
      pListOrdersData[0]?.orderNo, pListOrdersData[0]?.shipmentLines?.length, pListId]);

  // Load dropdown data (delivery methods and pack zones)
  useEffect(() => {
    const loadDropdownData = async () => {
      try {
        const [methods, zones] = await Promise.all([
          packingService.getDeliveryMethods(),
          packingService.getPackZones()
        ]);
        setDeliveryMethods(methods);
        setPackZones(zones);
      } catch (err) {
        console.error('Error loading dropdown data:', err);
      }
    };

    if (isOpen) {
      loadDropdownData();
    }
  }, [isOpen]);

  // Initialize pListData with first order's deliveryMethodNo when available
  useEffect(() => {
    if (pListOrders.length > 0 && deliveryMethods.length > 0) {
      const firstOrder = pListOrders[0];
      const orderDeliveryMethodNo = firstOrder.deliveryMethodNo;
      if (orderDeliveryMethodNo && !pListData.deliveryMethodNo) {
        const method = deliveryMethods.find(m => m.deliveryMethodNo === orderDeliveryMethodNo);
        setPListData(prev => ({
          ...prev,
          deliveryMethodNo: orderDeliveryMethodNo,
          carrierName: method?.name || prev.carrierName
        }));
      }
    }
  }, [pListOrders, deliveryMethods]);

  // Sync pListOrdersData when pListOrders prop changes
  useEffect(() => {
    setPListOrdersData(
      pListOrders.map(order => ({
        orderNo: order.orderNo,
        totalVolume: order.totalVolume?.toString() || '',
        totalWeight: order.totalWeight?.toString() || '',
        numOfPackages: order.numOfPackages?.toString() || '',
        numOfPallets: order.numOfPallets?.toString() || '',
        carrierName: order.carrierName || '',
        deliveryMethodNo: order.deliveryMethodNo ?? null,
        packZoneID: order.packZoneID || '',
        packageRemark: order.packageRemark || '',
        expanded: false,
        shipmentLinesExpanded: false,
        shipmentLines: [],
        customerName: order.customerName,
        delAddress1: order.delAddress1,
        delCity: order.delCity,
        delCountry: order.delCountry
      }))
    );
  }, [pListOrders]);

  // Load existing shipment lines when modal opens
  useEffect(() => {
    const loadShipmentLines = async () => {
      if (!isOpen || pListOrders.length === 0) return;

      setIsLoadingShipmentLines(true);
      try {
        const shipmentLinesPromises = pListOrders.map(order =>
          shipmentLineService.getByPListOrder(pListId, order.orderNo)
            .then(lines => ({ orderNo: order.orderNo, lines }))
            .catch(() => ({ orderNo: order.orderNo, lines: [] as ShipmentLineDto[] }))
        );

        const results = await Promise.all(shipmentLinesPromises);

        setPListOrdersData(prev => prev.map(order => {
          const result = results.find(r => r.orderNo === order.orderNo);
          if (result && result.lines.length > 0) {
            const mappedLines: ShipmentLineRow[] = result.lines.map(line => ({
              lineID: line.lineID,
              transitPackagingID: line.transitPackagingID,
              transitPackagingDescr: line.transitPackagingDescr,
              description1: line.description1,
              numOfPackages: line.numOfPackages,
              volume: line.volume,
              weight: line.weight,
              height: line.height,
              width: line.width,
              length: line.length,
              pListID: line.pListID,
              orderNo: line.orderNo,
              sorting: line.sorting,
              isNew: false
            }));
            return { ...order, shipmentLines: mappedLines };
          }
          return order;
        }));
      } catch (error) {
        console.error('Failed to load shipment lines:', error);
      } finally {
        setIsLoadingShipmentLines(false);
      }
    };

    loadShipmentLines();
  }, [isOpen, pListId, pListOrders]);

  if (!isOpen) return null;

  const handlePListDataChange = (field: keyof DeliveryInfoData, value: string | number | null) => {
    setPListData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePListOrderDataChange = (orderNo: number, field: keyof DeliveryInfoData, value: string | number | null) => {
    setPListOrdersData(prev =>
      prev.map(order =>
        order.orderNo === orderNo
          ? { ...order, [field]: value }
          : order
      )
    );
  };

  const toggleOrderExpanded = (orderNo: number) => {
    setPListOrdersData(prev =>
      prev.map(order =>
        order.orderNo === orderNo
          ? { ...order, expanded: !order.expanded }
          : order
      )
    );
  };

  const handleShipmentLinesChange = (orderNo: number, lines: ShipmentLineRow[]) => {
    setPListOrdersData(prev =>
      prev.map(order =>
        order.orderNo === orderNo
          ? { ...order, shipmentLines: lines }
          : order
      )
    );
  };

  const handleShipmentLinesExpandedChange = (orderNo: number, expanded: boolean) => {
    setPListOrdersData(prev =>
      prev.map(order =>
        order.orderNo === orderNo
          ? { ...order, shipmentLinesExpanded: expanded }
          : order
      )
    );
  };

  const handleModeSelect = (mode: 'consolidate' | 'per-order') => {
    setDeliveryMode(mode);
    if (mode === 'consolidate') {
      setShowAggregationSelection(true);
    } else {
      setShowAggregationSelection(false);
      setAggregationMethod(null);
    }
  };

  const handleAggregationSelect = (method: AggregationMethod) => {
    setAggregationMethod(method);
    setShowAggregationSelection(false);
  };

  const validateRequiredFields = (): string | null => {
    const errors: string[] = [];

    const perOrderMode = deliveryMode === 'per-order' || deliveryMode === 'forced-per-order';

    if (perOrderMode) {
      // PER ORDRE-MODUS: hodefeltene (pListData) rendres IKKE i denne visningen — kravsjekkene
      // må derfor gå mot HVER ordres egne felter (de som faktisk står på skjermen). Tidligere
      // sjekket koden pListData også her, som blokkerte avslutning mot et usynlig/tomt felt
      // (rapportert av Norlux 2026-07-02). Ordre-egne kolli-linjer waiver vekt/volum/kolli for
      // den ordren (samme regel som ellers); paller waives aldri (ren ERP-/fakturerings-info).
      for (const order of pListOrdersData) {
        const orderLabel = t('warehouse.modules.picking.delivery.orderNo', { 0: order.orderNo });
        const orderHasLines = (order.shipmentLines?.length ?? 0) > 0;
        if (!orderHasLines) {
          if (userProfile?.pListWeightRequired && !order.totalWeight) {
            errors.push(`${orderLabel}: ${t('warehouse.modules.picking.delivery.weightRequired')}`);
          }
          if (userProfile?.pListVolumeRequired && !order.totalVolume) {
            errors.push(`${orderLabel}: ${t('warehouse.modules.picking.delivery.volumeRequired')}`);
          }
          if (userProfile?.pListNumOfPackagesRequired && !order.numOfPackages) {
            errors.push(`${orderLabel}: ${t('warehouse.modules.picking.delivery.numOfPackagesRequired')}`);
          }
        }
        if (userProfile?.pListNumOfPalletsRequired && !order.numOfPallets) {
          errors.push(`${orderLabel}: ${t('warehouse.modules.picking.delivery.numOfPalletsRequired')}`);
        }
      }
    } else {
      // SINGLE/KONSOLIDERT: hodefeltene vises — sjekk dem som før. Kolli-linjer for gjeldende
      // scenario er den autoritative kilden; er de registrert kreves ikke kolli/vekt/volum
      // på hodenivå (samme regel som P11/P12).
      const scenarioLines: ShipmentLineRow[] =
        (deliveryMode === 'single' || scenario === 'single')
          ? (pListOrdersData[0]?.shipmentLines ?? [])
          : shipmentLinesForConsolidation;
      const hasDetailLines = scenarioLines.length > 0;

      if (!hasDetailLines) {
        if (userProfile?.pListWeightRequired && !pListData.totalWeight) {
          errors.push(t('warehouse.modules.picking.delivery.weightRequired'));
        }
        if (userProfile?.pListVolumeRequired && !pListData.totalVolume) {
          errors.push(t('warehouse.modules.picking.delivery.volumeRequired'));
        }
        if (userProfile?.pListNumOfPackagesRequired && !pListData.numOfPackages) {
          errors.push(t('warehouse.modules.picking.delivery.numOfPackagesRequired'));
        }
      }
      // Antall paller er IKKE dekket av kolli-linjer (ren ERP-/fakturerings-info) → beholdes uavhengig.
      if (userProfile?.pListNumOfPalletsRequired && !pListData.numOfPallets) {
        errors.push(t('warehouse.modules.picking.delivery.numOfPalletsRequired'));
      }
    }

    // Punkt 10: når valgt leveringsmåte har «krever kollidetaljer»-flagget, må kollidetalj-LINJER
    // (kollilinjer) være registrert før plukket kan avsluttes. Vekt kreves ikke lenger på header —
    // kolli-linjene er den autoritative kilden (samme regel som P11/P12).
    const requiresDetails = (methodNo: number | null | undefined) =>
      !!deliveryMethods.find(m => m.deliveryMethodNo === methodNo)?.requirePackageDetailsYesNo;
    const detailsMsg = t('warehouse.modules.picking.delivery.packageDetailsRequired', {}, 'Valgt leveringsmåte krever kollidetaljer (kollilinjer) før plukket kan avsluttes.');

    if (deliveryMode === 'single' || scenario === 'single') {
      if (requiresDetails(pListData.deliveryMethodNo) && (pListOrdersData[0]?.shipmentLines?.length ?? 0) === 0) {
        errors.push(detailsMsg);
      }
    } else if (deliveryMode === 'consolidate' && aggregationMethod) {
      if (requiresDetails(pListData.deliveryMethodNo) && shipmentLinesForConsolidation.length === 0) {
        errors.push(detailsMsg);
      }
    } else {
      for (const order of pListOrdersData) {
        if (requiresDetails(order.deliveryMethodNo) && (order.shipmentLines?.length ?? 0) === 0) {
          errors.push(detailsMsg);
          break;
        }
      }
    }

    if (errors.length > 0) {
      return t('warehouse.modules.picking.delivery.requiredFieldsHeader') + '\n' + errors.join('\n');
    }

    return null;
  };

  // Punkt 11: sjekk om sum av kollidetaljer avviker fra header-totalen (samme regel som pakkemodulen).
  // Antall kolli må stemme eksakt; vekt/volum tillates liten epsilon (abs > 0.01 OG relativ > 1 %).
  // Returnerer true ved avvik → vises som advarsel (ikke hard blokk) i handleSubmit.
  const hasPackageDetailsMismatch = (): boolean => {
    const offBy = (a: number, b: number) => Math.abs(a - b) > 0.01 && Math.abs(a - b) / b > 0.01;
    const lineMismatch = (
      lines: ShipmentLineRow[] | undefined,
      packagesStr: string,
      weightStr: string,
      volumeStr: string
    ): boolean => {
      if (!lines || lines.length === 0) return false;
      const sumPackages = lines.reduce((s, l) => s + (Number(l.numOfPackages) || 0), 0);
      const sumWeight = lines.reduce((s, l) => s + (Number(l.weight) || 0) * (Number(l.numOfPackages) || 0), 0);
      const sumVolume = lines.reduce((s, l) => s + (Number(l.volume) || 0) * (Number(l.numOfPackages) || 0), 0);
      const headerPackages = parseInt(packagesStr || '') || 0;
      const headerWeight = parseFloat(weightStr || '') || 0;
      const headerVolume = parseFloat(volumeStr || '') || 0;
      return (
        (headerPackages > 0 && sumPackages !== headerPackages) ||
        (headerWeight > 0 && offBy(sumWeight, headerWeight)) ||
        (headerVolume > 0 && offBy(sumVolume, headerVolume))
      );
    };

    if (deliveryMode === 'single' || scenario === 'single') {
      return lineMismatch(pListOrdersData[0]?.shipmentLines, pListData.numOfPackages, pListData.totalWeight, pListData.totalVolume);
    }
    if (deliveryMode === 'consolidate' && aggregationMethod) {
      return lineMismatch(shipmentLinesForConsolidation, pListData.numOfPackages, pListData.totalWeight, pListData.totalVolume);
    }
    return pListOrdersData.some(o => lineMismatch(o.shipmentLines, o.numOfPackages, o.totalWeight, o.totalVolume));
  };

  const handleSubmit = async () => {
    try {
      // Validate required fields
      const validationError = validateRequiredFields();
      if (validationError) {
        alert(validationError);
        return;
      }

      // Punkt 11: advarsel (ikke blokk) hvis sum av kollidetaljer ikke stemmer med header-totalen.
      if (hasPackageDetailsMismatch() && !window.confirm(t('warehouse.modules.packing.validation.packageDetailsMismatch'))) {
        return;
      }

      setIsSubmitting(true);

      // Convert pListOrdersData to Map
      const ordersMap = new Map<number, DeliveryInfoData>();
      const shipmentLinesMap = new Map<number, ShipmentLineRow[]>();

      // Handle different delivery modes
      if (deliveryMode === 'single' || scenario === 'single') {
        // 1:1 scenario - auto-copy PList data to the single order
        const singleOrder = pListOrdersData[0];
        if (singleOrder) {
          ordersMap.set(singleOrder.orderNo, {
            totalVolume: pListData.totalVolume,
            totalWeight: pListData.totalWeight,
            numOfPackages: pListData.numOfPackages,
            numOfPallets: pListData.numOfPallets,
            carrierName: pListData.carrierName,
            deliveryMethodNo: pListData.deliveryMethodNo,
            packZoneID: pListData.packZoneID,
            packageRemark: pListData.packageRemark
          });

          // Use per-order shipment lines if any
          if (singleOrder.shipmentLines && singleOrder.shipmentLines.length > 0) {
            shipmentLinesMap.set(singleOrder.orderNo, singleOrder.shipmentLines);
          }
        }
      } else if (deliveryMode === 'consolidate' && aggregationMethod) {
        // Consolidation mode - apply PList data to all orders
        pListOrdersData.forEach(order => {
          if (aggregationMethod === 'copy') {
            // Copy identical values to all orders
            ordersMap.set(order.orderNo, {
              totalVolume: pListData.totalVolume,
              totalWeight: pListData.totalWeight,
              numOfPackages: pListData.numOfPackages,
              numOfPallets: pListData.numOfPallets,
              carrierName: pListData.carrierName,
              deliveryMethodNo: pListData.deliveryMethodNo,
              packZoneID: pListData.packZoneID,
              packageRemark: pListData.packageRemark
            });
          } else {
            // Divide values equally among orders
            const numOrders = pListOrdersData.length;
            const dividedVolume = pListData.totalVolume ? (parseFloat(pListData.totalVolume) / numOrders).toFixed(4) : '';
            const dividedWeight = pListData.totalWeight ? (parseFloat(pListData.totalWeight) / numOrders).toFixed(2) : '';
            const dividedPackages = pListData.numOfPackages ? Math.ceil(parseInt(pListData.numOfPackages) / numOrders).toString() : '';
            const dividedPallets = pListData.numOfPallets ? (parseFloat(pListData.numOfPallets) / numOrders).toFixed(2) : '';

            ordersMap.set(order.orderNo, {
              totalVolume: dividedVolume,
              totalWeight: dividedWeight,
              numOfPackages: dividedPackages,
              numOfPallets: dividedPallets,
              carrierName: pListData.carrierName,
              deliveryMethodNo: pListData.deliveryMethodNo,
              packZoneID: pListData.packZoneID,
              packageRemark: pListData.packageRemark
            });
          }
        });

        // For consolidation, shipment lines go to the first order (will be linked to Shipment)
        if (shipmentLinesForConsolidation.length > 0) {
          shipmentLinesMap.set(pListOrdersData[0].orderNo, shipmentLinesForConsolidation);
        }
      } else {
        // Per-order mode - use individual order data
        pListOrdersData.forEach(order => {
          ordersMap.set(order.orderNo, {
            totalVolume: order.totalVolume,
            totalWeight: order.totalWeight,
            numOfPackages: order.numOfPackages,
            numOfPallets: order.numOfPallets,
            carrierName: order.carrierName,
            deliveryMethodNo: order.deliveryMethodNo,
            packZoneID: order.packZoneID,
            packageRemark: order.packageRemark
          });

          // Include shipment lines if any exist
          if (order.shipmentLines && order.shipmentLines.length > 0) {
            shipmentLinesMap.set(order.orderNo, order.shipmentLines);
          }
        });
      }

      const shouldConsolidate = deliveryMode === 'consolidate' && aggregationMethod !== null;
      await onConfirm(
        pListData,
        ordersMap,
        shipmentLinesMap.size > 0 ? shipmentLinesMap : undefined,
        shouldConsolidate
      );
    } catch (error) {
      console.error('Error submitting delivery info:', error);
      // Oversatt feilkode fra backend når tilgjengelig; ellers samme generiske melding som før
      alert(translateApiError(error, t) ?? t('warehouse.modules.picking.delivery.saveFailed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderDeliveryInfoFields = (
    data: DeliveryInfoData,
    onChange: (field: keyof DeliveryInfoData, value: string | number | null) => void,
    readOnly: boolean = false,
    // Lastbærer-fokus (36334219): kolli/paller/vekt/volum beregnes fra kollilinjene og låses
    aggregatedLock: boolean = false
  ) => (
    <div className="grid grid-cols-1 min-[420px]:grid-cols-2 gap-3 min-[420px]:gap-4">
      {aggregatedLock && (
        <div className="min-[420px]:col-span-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-xs text-blue-800">
          {t('warehouse.modules.picking.delivery.aggregatedFromLines', {},
            'Kolli, paller, vekt og volum beregnes automatisk fra Transport-detaljer.')}
        </div>
      )}
      {/* 1. Antall kolli */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('warehouse.modules.picking.delivery.numOfPackages')}{userProfile?.pListNumOfPackagesRequired && <span className="text-red-600">*</span>}
        </label>
        <input
          type="number"
          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
            userProfile?.pListNumOfPackagesRequired && !data.numOfPackages ? 'border-red-300' : 'border-gray-300'
          } ${aggregatedLock ? 'bg-gray-50 text-gray-600' : ''}`}
          value={data.numOfPackages}
          onChange={(e) => onChange('numOfPackages', e.target.value)}
          readOnly={readOnly || aggregatedLock}
          required={userProfile?.pListNumOfPackagesRequired}
        />
      </div>

      {/* 2. Antall paller */}
      <div>
        {/* Tablet-vennlig: lite trykkbart (?) som viser hjelpeteksten ved trykk (ikke hover). */}
        <label className="flex items-center gap-1 text-sm font-medium text-gray-700 mb-1">
          <span>{t('warehouse.modules.picking.delivery.numOfPallets')}{userProfile?.pListNumOfPalletsRequired && <span className="text-red-600">*</span>}</span>
          <HelpHint
            label={t('warehouse.modules.picking.delivery.numOfPallets')}
            text={t('warehouse.modules.packing.fields.numberOfPalletsTooltip', {}, 'Brukes kun for ERP-rapportering og fakturering (blir vanligvis ordretilleggslinjer). Sendes ikke til transportøren (TA).')}
          />
        </label>
        <input
          type="number"
          step="0.01"
          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
            userProfile?.pListNumOfPalletsRequired && !data.numOfPallets ? 'border-red-300' : 'border-gray-300'
          }`}
          value={data.numOfPallets}
          onChange={(e) => onChange('numOfPallets', e.target.value)}
          readOnly={readOnly || aggregatedLock}
          required={userProfile?.pListNumOfPalletsRequired}
        />
      </div>

      {/* 3. Vekt */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('warehouse.modules.picking.delivery.weightKg')}{userProfile?.pListWeightRequired && <span className="text-red-600">*</span>}
        </label>
        <input
          type="number"
          step="0.01"
          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
            userProfile?.pListWeightRequired && !data.totalWeight ? 'border-red-300' : 'border-gray-300'
          }`}
          value={data.totalWeight}
          onChange={(e) => onChange('totalWeight', e.target.value)}
          readOnly={readOnly || aggregatedLock}
          required={userProfile?.pListWeightRequired}
        />
      </div>

      {/* 4. Volum */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('warehouse.modules.picking.delivery.volumeM3')}{userProfile?.pListVolumeRequired && <span className="text-red-600">*</span>}
        </label>
        <input
          type="number"
          step="0.0001"
          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
            userProfile?.pListVolumeRequired && !data.totalVolume ? 'border-red-300' : 'border-gray-300'
          }`}
          value={data.totalVolume}
          onChange={(e) => onChange('totalVolume', e.target.value)}
          readOnly={readOnly || aggregatedLock}
          required={userProfile?.pListVolumeRequired}
        />
      </div>

      {/* 5. Transportør (dropdown fra DeliveryMethods) */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('warehouse.modules.picking.delivery.carrier')}
        </label>
        {readOnly ? (
          <input
            type="text"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
            value={data.carrierName}
            readOnly
          />
        ) : (
          <SearchableSelect
            value={data.deliveryMethodNo}
            options={deliveryMethods.map((method) => ({ value: method.deliveryMethodNo, label: method.name }))}
            onChange={(val) => {
              const methodNo = val === null || val === '' ? null : Number(val);
              const method = deliveryMethods.find(m => m.deliveryMethodNo === methodNo);
              onChange('deliveryMethodNo', methodNo);
              onChange('carrierName', method?.name || '');
            }}
            placeholder={t('warehouse.modules.picking.delivery.selectCarrier')}
            searchPlaceholder={t('warehouse.modules.picking.delivery.searchCarrier', {}, 'Søk transportør...')}
            clearText={t('warehouse.modules.picking.delivery.selectCarrier')}
            noResultsText={t('warehouse.modules.picking.delivery.noCarrierMatch', {}, 'Ingen transportør matcher søket')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent min-h-[42px]"
          />
        )}
      </div>

      {/* 6. Lastesone (dropdown fra PackZones) */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('warehouse.modules.picking.delivery.loadingZone')}
        </label>
        {readOnly ? (
          <input
            type="text"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
            value={data.packZoneID}
            readOnly
          />
        ) : (
          <select
            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent min-h-[42px]"
            value={data.packZoneID}
            onChange={(e) => onChange('packZoneID', e.target.value)}
          >
            <option value="">{t('warehouse.modules.picking.delivery.selectPackZone')}</option>
            {packZones.map((zone) => (
              <option key={zone.packZoneID} value={zone.packZoneID}>
                {zone.description || zone.packZoneID}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* 7. Pakkemerknad */}
      <div className="min-[420px]:col-span-2">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('warehouse.modules.picking.delivery.packageRemark')}
        </label>
        <textarea
          rows={2}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder={t('warehouse.modules.picking.delivery.packageRemarkPlaceholder')}
          value={data.packageRemark}
          onChange={(e) => onChange('packageRemark', e.target.value)}
          readOnly={readOnly}
        />
      </div>
    </div>
  );

  // Render mode selection for multi-order same address
  const renderModeSelection = () => (
    <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
      <h4 className="text-lg font-semibold text-blue-900 mb-2">
        {t('warehouse.modules.picking.delivery.ordersSameAddress', { 0: pListOrders.length })}
      </h4>
      <p className="text-sm text-blue-700 mb-4">
        {t('warehouse.modules.picking.delivery.sameAddressPrompt')}
      </p>
      <div className="flex flex-col sm:flex-row gap-3">
        <button
          onClick={() => handleModeSelect('consolidate')}
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition-colors ${
            deliveryMode === 'consolidate'
              ? 'border-blue-600 bg-blue-100 text-blue-900'
              : 'border-gray-300 bg-white hover:border-blue-400 text-gray-700'
          }`}
        >
          <Users size={20} />
          <span className="font-medium">{t('warehouse.modules.picking.delivery.consolidate')}</span>
        </button>
        <button
          onClick={() => handleModeSelect('per-order')}
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition-colors ${
            deliveryMode === 'per-order'
              ? 'border-blue-600 bg-blue-100 text-blue-900'
              : 'border-gray-300 bg-white hover:border-blue-400 text-gray-700'
          }`}
        >
          <User size={20} />
          <span className="font-medium">{t('warehouse.modules.picking.delivery.perOrder')}</span>
        </button>
      </div>
    </div>
  );

  // Render aggregation method selection
  const renderAggregationSelection = () => (
    <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
      <h4 className="text-lg font-semibold text-yellow-900 mb-2">
        {t('warehouse.modules.picking.delivery.howDistributeValues')}
      </h4>
      <p className="text-sm text-yellow-700 mb-4">
        {t('warehouse.modules.picking.delivery.distributePrompt')}
      </p>
      <div className="flex flex-col sm:flex-row gap-3">
        <button
          onClick={() => handleAggregationSelect('copy')}
          className={`flex-1 px-4 py-3 rounded-lg border-2 transition-colors ${
            aggregationMethod === 'copy'
              ? 'border-yellow-600 bg-yellow-100 text-yellow-900'
              : 'border-gray-300 bg-white hover:border-yellow-400 text-gray-700'
          }`}
        >
          <div className="font-medium mb-1">{t('warehouse.modules.picking.delivery.copyIdentical')}</div>
          <div className="text-xs text-gray-500">{t('warehouse.modules.picking.delivery.copyIdenticalDescr')}</div>
        </button>
        <button
          onClick={() => handleAggregationSelect('divide')}
          className={`flex-1 px-4 py-3 rounded-lg border-2 transition-colors ${
            aggregationMethod === 'divide'
              ? 'border-yellow-600 bg-yellow-100 text-yellow-900'
              : 'border-gray-300 bg-white hover:border-yellow-400 text-gray-700'
          }`}
        >
          <div className="font-medium mb-1">{t('warehouse.modules.picking.delivery.divideEqually')}</div>
          <div className="text-xs text-gray-500">{t('warehouse.modules.picking.delivery.divideEquallyDescr', { 0: pListOrders.length })}</div>
        </button>
      </div>
    </div>
  );

  // Render warning for different addresses
  const renderDifferentAddressWarning = () => (
    <div className="mb-6 p-4 bg-orange-50 border border-orange-200 rounded-lg">
      <div className="flex items-start gap-3">
        <AlertTriangle className="text-orange-600 flex-shrink-0 mt-0.5" size={20} />
        <div>
          <h4 className="text-lg font-semibold text-orange-900 mb-1">
            {t('warehouse.modules.picking.delivery.differentAddresses')}
          </h4>
          <p className="text-sm text-orange-700">
            {t('warehouse.modules.picking.delivery.differentAddressesDescr')}
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-0 sm:p-4 overflow-y-auto">
      <div className="bg-white w-full h-full sm:h-auto sm:rounded-lg sm:max-w-4xl sm:my-8">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200">
          <h3 className="text-lg sm:text-xl font-semibold">{t('warehouse.modules.picking.delivery.title')}</h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-4 sm:p-6 max-h-[calc(100vh-140px)] sm:max-h-[calc(100vh-200px)] overflow-y-auto">
          {/* Scenario: Different addresses warning */}
          {scenario === 'multi-different-address' && renderDifferentAddressWarning()}

          {/* Scenario: Multi-order same address - mode selection */}
          {scenario === 'multi-same-address' && showModeSelection && renderModeSelection()}

          {/* Aggregation method selection for consolidation */}
          {deliveryMode === 'consolidate' && showAggregationSelection && renderAggregationSelection()}

          {/* PList Section - shown for single order or consolidation mode */}
          {(scenario === 'single' || scenario === 'no-orders' || (deliveryMode === 'consolidate' && aggregationMethod)) && (
            <div className="mb-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <h4 className="text-lg font-semibold text-blue-900 mb-4">
                  {scenario === 'single' ? t('warehouse.modules.picking.delivery.pickingListSingleOrder', { 0: pListId }) :
                   deliveryMode === 'consolidate' ? t('warehouse.modules.picking.delivery.consolidateHeader', { 0: pListOrders.length }) :
                   t('warehouse.modules.picking.delivery.pickingListHeader', { 0: pListId })}
                </h4>
                {scenario === 'single' && pListOrders[0] && (
                  <p className="text-sm text-blue-700 mb-4">
                    {t('warehouse.modules.picking.delivery.orderNo', { 0: pListOrders[0].orderNo })} - {pListOrders[0].customerName || t('warehouse.modules.picking.delivery.unknownCustomer')}
                    {pListOrders[0].delAddress1 && `, ${pListOrders[0].delAddress1}`}
                    {pListOrders[0].delCity && `, ${pListOrders[0].delCity}`}
                  </p>
                )}
                {deliveryMode === 'consolidate' && (
                  <p className="text-sm text-blue-700 mb-4">
                    {t('warehouse.modules.picking.delivery.commonDeliveryAddress')}: {pListOrders[0]?.delAddress1 || t('common.labels.unknown')}, {pListOrders[0]?.delCity || ''}
                  </p>
                )}
                {renderDeliveryInfoFields(pListData, handlePListDataChange, false, headAggregatedLock)}

                {/* ShipmentLine editor for single order or consolidation */}
                <div className="mt-4">
                  {scenario === 'single' && pListOrders[0] ? (
                    <ShipmentLineEditor
                      pListId={pListId}
                      orderNo={pListOrders[0].orderNo}
                      initialLines={pListOrdersData[0]?.shipmentLines.map(sl => ({
                        ...sl,
                        id: 0,
                        shipmentID: 0,
                        lineStatus: 0
                      } as ShipmentLineDto)) || []}
                      onChange={(lines) => handleShipmentLinesChange(pListOrders[0].orderNo, lines)}
                      expanded={isSingleParcelMode || pListOrdersData[0]?.shipmentLinesExpanded}
                      onExpandedChange={(expanded) => handleShipmentLinesExpandedChange(pListOrders[0].orderNo, expanded)}
                      disabled={isSubmitting}
                      singleParcelMode={isSingleParcelMode}
                    />
                  ) : deliveryMode === 'consolidate' ? (
                    <ShipmentLineEditor
                      pListId={pListId}
                      orderNo={pListOrders[0]?.orderNo || 0}
                      initialLines={shipmentLinesForConsolidation.map(sl => ({
                        ...sl,
                        id: 0,
                        shipmentID: 0,
                        lineStatus: 0
                      } as ShipmentLineDto))}
                      onChange={setShipmentLinesForConsolidation}
                      disabled={isSubmitting}
                    />
                  ) : null}
                </div>
              </div>
            </div>
          )}

          {/* PListOrders Section - shown for per-order mode or forced per-order */}
          {(deliveryMode === 'per-order' || deliveryMode === 'forced-per-order') && pListOrders.length > 0 && (
            <div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3">
                {t('warehouse.modules.picking.delivery.ordersCount', { 0: pListOrders.length })}
              </h4>
              <p className="text-sm text-gray-600 mb-4">
                {t('warehouse.modules.picking.delivery.clickOrderToRegister')}
              </p>

              <div className="space-y-3">
                {pListOrdersData.map((order) => (
                  <div
                    key={order.orderNo}
                    className="border border-gray-200 rounded-lg overflow-hidden"
                  >
                    {/* Order Header */}
                    <button
                      onClick={() => toggleOrderExpanded(order.orderNo)}
                      className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
                    >
                      <div className="text-left">
                        <span className="font-medium text-gray-900">
                          {t('warehouse.modules.picking.delivery.orderNo', { 0: order.orderNo })}
                        </span>
                        <span className="text-sm text-gray-500 ml-2">
                          {order.customerName || t('warehouse.modules.picking.delivery.unknownCustomer')}
                        </span>
                        {order.delAddress1 && (
                          <div className="text-xs text-gray-400">
                            {order.delAddress1}, {order.delCity}
                          </div>
                        )}
                      </div>
                      {order.expanded ? (
                        <ChevronUp size={20} className="text-gray-600" />
                      ) : (
                        <ChevronDown size={20} className="text-gray-600" />
                      )}
                    </button>

                    {/* Order Content */}
                    {order.expanded && (
                      <div className="p-4 bg-white space-y-4">
                        {renderDeliveryInfoFields(
                          order,
                          (field, value) => handlePListOrderDataChange(order.orderNo, field, value),
                          false,
                          carrierLineFocus && (order.shipmentLines?.length ?? 0) > 0
                        )}

                        {/* Transport-detaljer (ShipmentLine) */}
                        <div className="mt-4">
                          <ShipmentLineEditor
                            pListId={pListId}
                            orderNo={order.orderNo}
                            initialLines={order.shipmentLines.map(sl => ({
                              ...sl,
                              id: 0,
                              shipmentID: 0,
                              lineStatus: 0
                            } as ShipmentLineDto))}
                            onChange={(lines) => handleShipmentLinesChange(order.orderNo, lines)}
                            expanded={order.shipmentLinesExpanded}
                            onExpandedChange={(expanded) => handleShipmentLinesExpandedChange(order.orderNo, expanded)}
                            disabled={isSubmitting}
                            singleParcelMode={isSingleParcelMode}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* No orders message */}
          {pListOrders.length === 0 && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <p className="text-sm text-gray-600">
                {t('warehouse.modules.picking.delivery.noOrderRelations')}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-3 p-4 sm:p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-6 py-3 sm:py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors min-h-[44px] order-2 sm:order-1"
            disabled={isSubmitting}
          >
            {t('common.buttons.cancel')}
          </button>
          <button
            onClick={handleSubmit}
            className="px-6 py-3 sm:py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed min-h-[44px] order-1 sm:order-2"
            disabled={isSubmitting || (deliveryMode === 'consolidate' && !aggregationMethod)}
          >
            {isSubmitting ? t('common.status.saving') : t('common.buttons.confirm')}
          </button>
        </div>
      </div>
    </div>
  );
};
